import { useInspectionId } from "../../hooks/useInspectionId";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import { useEffect, useState } from "react";
import { updateInspectionAdjustedGrades, fetchInspectionData, fetchRecalculateAdjustedGrades } from "../../store/thunks/inspectionThunks";
import { selectInspection } from "../../store/selectors/inspectionSelectors";
import { InspectionInfoCard } from "../Inspection/InspectionInfoCard";
import { PageHeader } from "../Shared/PageHeader";
import { Button } from "primereact/button";
import { AdjustableGrade } from "./AdjustableGrade";
import "./AdjustedGradeEditor.css";
import { Grade } from "../../types/inspection";
import { getGradingSections } from "../../services/gradingService";
import {
    adjustedGradesDto,
    combineAdjustedGrades,
    GRADE_TYPE_CONDITION,
    GRADE_TYPE_MANAGEMENT,
    SectionOrder,
    sectionOrderFromSections,
    sortGradesForDisplay,
} from "../../utils/GradeUtils";

export const AdjustedGradeEditor = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation("locale");
    const { inspection, loading, adjustedGrades: existingAdjustedGrades, errorAdjustedGrades, loadingAdjustedGrades } = useAppSelector(selectInspection);
    const [adjustedIdwalGrade, setAdjustedIdwalGrade] = useState<number | null>(0);
    const [adjustedConditionGrade, setAdjustedConditionGrade] = useState<number | null>(0);
    const [adjustedManagementGrade, setAdjustedManagementGrade] = useState<number | null>(0);
    const [adjustedGrades, setAdjustedGrades] = useState<Grade[] | null>([]);
    const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
    const [sectionOrder, setSectionOrder] = useState<SectionOrder>(new Map());

    const inspectionId = useInspectionId();

    useEffect(() => {
        if (inspectionId && !inspection) {
            dispatch(fetchInspectionData(inspectionId));
        }
    }, [inspectionId]);

    // The section template is the fallback ordering for a get-by-id payload that
    // carries no `order`. Failures are already toasted by the service and leave
    // the map empty, which costs the fallback, not the page.
    useEffect(() => {
        if (!inspectionId) {
            return;
        }

        let current = true;

        getGradingSections(inspectionId).then((sections) => {
            if (current) {
                setSectionOrder(sectionOrderFromSections(sections));
            }
        });

        return () => {
            current = false;
        };
    }, [inspectionId]);

    useEffect(() => {
        if (inspection && !loadingAdjustedGrades) {
            const combinedGrades = combineAdjustedGrades(inspection?.grades ?? [], existingAdjustedGrades?.adjustedGrades ?? []);
            setAdjustedGrades(combinedGrades);

            if (existingAdjustedGrades?.adjustedGrades?.length) {
                setAdjustedIdwalGrade(existingAdjustedGrades.calculatedGrades.overallGrade);
                setAdjustedConditionGrade(existingAdjustedGrades.calculatedGrades.conditionGrade.computedConditionGrade);
                setAdjustedManagementGrade(existingAdjustedGrades.calculatedGrades.managementGrade.computedManagementGrade);
            } else {
                setAdjustedIdwalGrade(inspection?.overallScore ?? 0);
                setAdjustedConditionGrade(inspection?.conditionScore ?? 0);
                setAdjustedManagementGrade(inspection?.managementScore ?? 0);
            }
        }
    }, [existingAdjustedGrades, loadingAdjustedGrades]);

    const titlesBar = () => {
        return (
            <div className="col-12 mt-4">
                <div className="flex justify-content-between align-items-center">
                    <div className="small-text ml-5">Grading areas</div>
                    <div className="flex gap-8 mr-7">
                        <span className="small-text">Original</span>
                        <span className="small-text ml-6">Adjusted</span>
                    </div>
                </div>
            </div>
        )
    }

    const onGradeAdjust = (score: number, vesselGradeId: number) => {
        if (saveDisabled) {
            setSaveDisabled(false);
        }

        if (!inspectionId || !adjustedGrades?.length) {
            console.warn("No adjusted grades to save");
            return;
        }

        const newAdjustedGrades = adjustedGrades?.map(grade => {
            if (grade.vesselGradeId === vesselGradeId) {
                return {
                    ...grade,
                    adjustedPoint: score / 10
                }
            }
            return grade;
        });

        const payloadBody = adjustedGradesDto(newAdjustedGrades);
        dispatch(fetchRecalculateAdjustedGrades(inspectionId, payloadBody));
    }

    const saveAdjustedGrades = async () => {
        setSaveDisabled(true);

        if (!inspectionId || !adjustedGrades?.length) {
            console.warn("No adjusted grades to save");
            return;
        }

        const payloadBody = adjustedGradesDto(adjustedGrades);
        dispatch(updateInspectionAdjustedGrades(inspectionId, payloadBody));
    }

    const editableGrades = (type: number) => {
        if (!inspection?.grades) {
            return null;
        }

        // The payload has no guaranteed order; sections graded after the first save arrive last.
        const ordered = sortGradesForDisplay(
            adjustedGrades?.filter(grade => grade.type === type && grade.point !== null) ?? [],
            sectionOrder
        );

        return ordered
            .map(grade => {
                const originalGrade = grade.point * 10;
                const adjustedGrade = (grade.adjustedPoint ?? grade.point) * 10;
                return (
                    <div key={grade.vesselGradeId} className="col-12 grade-entry" data-testid="grade-entry">
                        <AdjustableGrade vesselGradeId={grade.vesselGradeId} name={grade.name} originalGrade={originalGrade} adjustedGrade={adjustedGrade} adjustable onAdjust={onGradeAdjust} />
                    </div>
                )
            });
    }

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!inspection?.grades && errorAdjustedGrades) {
        return <div>Error loading inspection grading data</div>
    }

    const hasAdjustableGrades = adjustedGrades?.some(grade => grade.point !== null && grade.point >= 0);

    const displayGrades = () => {
        return (
            <div className="col-12 adjustable-grades mt-4">
                {titlesBar()}
                <div className="px-2 mb-2">
                    <AdjustableGrade name={"Idwal Grade"} originalGrade={inspection?.overallScore ?? 0} adjustedGrade={adjustedIdwalGrade} adjustable={false} mainGrade disabled={loadingAdjustedGrades} />
                </div>
                <div className="px-2 mb-2">
                    <AdjustableGrade name={"Condition Grade"} originalGrade={inspection?.conditionScore ?? 0} adjustedGrade={adjustedConditionGrade} adjustable={false} mainGrade disabled={loadingAdjustedGrades} />
                </div>
                <div className="px-2">
                    <AdjustableGrade name={"Management Grade"} originalGrade={inspection?.managementScore ?? 0} adjustedGrade={adjustedManagementGrade} adjustable={false} mainGrade disabled={loadingAdjustedGrades} />
                </div>
                {/* One type per column, so each column reads top to bottom in section order.
                    Below lg they stack, so each type keeps a bar of its own. */}
                <div className="flex flex-wrap">
                    <div id="sub-grade-areas" className="col-12 lg:col-6 p-0" data-testid="condition-grades">
                        {titlesBar()}
                        {editableGrades(GRADE_TYPE_CONDITION)}
                    </div>
                    <div id="other-grade-areas" className="col-12 lg:col-6 p-0" data-testid="management-grades">
                        {titlesBar()}
                        {editableGrades(GRADE_TYPE_MANAGEMENT)}
                    </div>
                </div>
                <div className="flex justify-content-end mt-3 mr-3 mb-3">
                    <Button label="Save changes" disabled={saveDisabled} onClick={saveAdjustedGrades}/>
                </div>
            </div>
        )
    }

    const displayNoGrades = () => {
        if (loadingAdjustedGrades) {
            return <div>Loading...</div>;
        }

        return (
            <div className="col-12 adjustable-grades mt-4">
                <div className="flex align-items-center justify-content-center m-8">
                    <span className="font-semibold">No grades to adjust</span>
                </div>
            </div>
        )
    }

    return (
        <div className="">
            <PageHeader title={t("adjustedGrade.title")} />
            <InspectionInfoCard thirdColumn="status" />
            {hasAdjustableGrades ? (displayGrades()) : (displayNoGrades())}
        </div>
    )
}